/*
 * output-filename.js — window.__outputName
 * ─────────────────────────────────────────────────────────────────────────────
 * Composes the name a tool gives to the file it hands back.
 *
 * WHY THIS EXISTS (bug-report 2026-08-21, /ru/audio-metadata, bez_paleva54):
 *   «Меняю имя исполнителя. В плеере появляется новое. В названии файла
 *    остаётся прежнее, неправильное. Что я делаю не так?»
 *   Nothing — the tool rewrote the tags and copied the input's name verbatim.
 *   A tag editor edits what a file SAYS IT IS; the filename is half of that
 *   identity for every user who browses files rather than a library. The tool
 *   even had the one-way bridge (filename → tags, «Разобрать имя файла») and
 *   no way back.
 *
 *   The same session shows the second half of the cost: 6 exports over 3 hours,
 *   every one of them named «Бородин Антон - Все кроме правды.mp3», so Android
 *   piled up …(1)…(2)…(3) and the user re-uploaded the STRIPPED copy by
 *   mistake. Measured across 30 days on that tool alone: 4686 of 8698 export
 *   sessions (53.9%) export twice or more under one constant name, 484 of them
 *   mix a tagged save with a strip — materially different files, one name.
 *   Class already recorded in reference_constant_output_filename_reads_as_broken:
 *   «имя выхода обязано нести материал и порядок».
 *
 * The rule this module encodes: the output name carries the MATERIAL. Derive it
 * from what the user just typed, sanitise it for every filesystem we can land
 * on, and never invent a name when the material is empty (fall back to the
 * input name — a wrong-looking name beats a meaningless one).
 *
 * UMD: browser (window.__outputName) + Node (module.exports) so it is testable
 * without a browser — same contract as audio-tag-writer.js.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.__outputName = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // Windows forbids \ / : * ? " < > | ; POSIX only forbids "/" and NUL, but a
    // file downloaded on Linux gets copied to a phone or a flash drive, so the
    // strictest set wins everywhere.
    var MAP_TO_DASH = /\s*[\\/:]\s*/g;    // separators read naturally as " - " (with the spaces around them)
    // Characters that must never reach a filename, checked by code point
    // rather than one dense escape class: wildcards, control chars, and the
    // BIDI overrides. A U+202E inside a tag makes the name RENDER
    // back-to-front in every file manager (the classic "...gpj.exe" spoof),
    // and tags travel with files from anywhere. Real Arabic/Hebrew text needs
    // none of these marks to display correctly.
    var WILDCARDS = [42, 63, 34, 60, 62, 124]; // * ? " < > |
    function dropBad(s) {
        var out = '';
        for (var i = 0; i < s.length; i++) {
            var c = s.charCodeAt(i);
            if (c < 32 || c === 127) continue;                  // control
            if (c >= 0x200b && c <= 0x200f) continue;           // zero-width, LRM/RLM
            if (c >= 0x202a && c <= 0x202e) continue;           // bidi embed/override
            if (c >= 0x2066 && c <= 0x2069) continue;           // bidi isolates
            if (c === 0xfeff) continue;                         // BOM
            if (WILDCARDS.indexOf(c) !== -1) continue;
            out += s[i];
        }
        return out;
    }
    // MS-DOS device names are still refused by Windows in any directory.
    var RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
    var MAX_BYTES = 180; // whole filename, UTF-8 — ext4/FAT cap at 255 bytes

    function utf8Len(s) {
        // TextEncoder is not guaranteed in every WebView we serve.
        var n = 0;
        for (var i = 0; i < s.length; i++) {
            var c = s.charCodeAt(i);
            if (c < 0x80) n += 1;
            else if (c < 0x800) n += 2;
            else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i++; }
            else n += 3;
        }
        return n;
    }

    function truncBytes(s, max) {
        if (utf8Len(s) <= max) return s;
        var out = '';
        for (var i = 0; i < s.length; i++) {
            var ch = s[i];
            // keep surrogate pairs whole
            var c = s.charCodeAt(i);
            if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) { ch += s[i + 1]; i++; }
            if (utf8Len(out + ch) > max) break;
            out += ch;
        }
        return out.replace(/[\s.\-–—_]+$/, '');
    }

    /** "a/b.mp3" → {base:"a/b", ext:"mp3"} (ext without the dot, '' if none). */
    function split(name) {
        var s = String(name == null ? '' : name);
        var m = s.match(/^(.*)\.([A-Za-z0-9]{1,8})$/);
        if (!m) return { base: s, ext: '' };
        return { base: m[1], ext: m[2] };
    }

    /**
     * Make one path SEGMENT safe. Never returns a string with a separator, a
     * trailing dot/space (Windows silently drops those) or a reserved name.
     */
    function sanitize(seg) {
        var s = String(seg == null ? '' : seg);
        // «;» WebKit (все браузеры iOS) читает как конец имени в Content-Disposition:
        // «Пост 2; смена» сохранялось как «Пост .png». Запятая читается так же.
        s = s.replace(/;/g, ',');
        s = dropBad(s);
        s = s.replace(/\s+/g, ' ');
        // A SEPARATOR becomes a dash spaced as the text implied it: "AC/DC" →
        // "AC-DC", but "Song: Part 2" → "Song - Part 2" (whitespace on either
        // side → on both). Only the separator: a hyphen the text already had
        // keeps its spacing. The rule used to respace every dash, and a
        // suspended hyphen is spaced on one side by design — «Flucht- und
        // Rettungsplan» came out as «Flucht - und Rettungsplan», «аудио- и
        // видеофайлы» as «аудио - и видеофайлы» (/evacuation-plan, de, 30.10).
        s = s.replace(MAP_TO_DASH, function (m) { return /\s/.test(m) ? ' - ' : '-'; });
        s = s.replace(/(\s*-\s*){2,}/g, ' - ');        // "a - - b" → "a - b"
        s = s.replace(/^[\s.\-–—_]+|[\s.\-–—_]+$/g, ''); // leading/trailing junk
        if (RESERVED.test(s)) s = '_' + s;
        return s;
    }

    /** Sanitised "base.ext", byte-capped, ext preserved verbatim. */
    /**
     * Собрать имя: `join(base, ext)`.
     *
     * `base` — строка ИЛИ массив частей, `ext` — строка, либо `{ext, sep}`,
     * либо (в трёхаргументной форме) `join(parts, sep, ext)`.
     *
     * ⚠️⚠️ Массив здесь появился не «для удобства», а потому что ИМЕННО ТАК
     * этот примитив зовут: замер 23.09.2026 по всем вызовам в дереве — 17 из
     * 40 передают массив, в трёх разных формах. Прежняя сигнатура принимала
     * только строку и массив молча склеивала через `String()`, то есть
     * ЗАПЯТОЙ, а `{ext:'png'}` превращала в расширение `.[object Object]`.
     * Прогон всех встреченных форм: **8 из 15 давали негодное имя**, из них
     * четыре — файл, который система не откроет ничем:
     *
     *     join(['scan','view'], {ext:'png'})   → "scan,view.[object Object]"
     *     join(['card','Анна'], '')            → "card,Анна"        (без расширения)
     *     join(['doc','a4'], '-', 'pdf')       → "doc,a4.-"
     *     join(['clip','01','0m12s'], 'mp4')   → "clip,01,0m12s.mp4"
     *
     * Из 17 массивных вызовов ЖИВЫМИ были три: `output-filename.js` просто не
     * подключён на остальных страницах, и там отрабатывала запасная ветка
     * вызывающего. То есть дефект был взведён в четырнадцати местах, а его
     * спусковой крючок — одна безобидная строка `<script>`, которую кто угодно
     * добавит завтра. Примитив, который почти половина вызывающих зовёт
     * неверно, — это не недисциплинированность вызывающих, а слишком узкий
     * контракт: он расширен, а не защищён запретом.
     *
     * Части чистятся ПООТДЕЛЬНОСТИ и пустые отбрасываются — иначе `null` из
     * `['card', who || null, occ]` превратился бы в отдельный сегмент.
     */
    function join(base, ext) {
        var sep = '-';
        if (arguments.length >= 3) {                       // join(parts, sep, ext)
            if (ext != null) sep = String(ext);
            ext = arguments[2];
        } else if (ext && typeof ext === 'object') {       // join(parts, {ext, sep})
            if (ext.sep != null) sep = String(ext.sep);
            ext = ext.ext;
        }
        var parts = Array.isArray(base) ? base : [base];
        var clean = [];
        for (var i = 0; i < parts.length; i++) {
            var seg = sanitize(parts[i]);
            if (seg) clean.push(seg);
        }
        var b = clean.join(sep);
        if (!b) return '';
        /* Склейка могла породить то, что чистилка уже отбраковывала бы в одной
           строке («a- -b», хвостовой дефис от пустой части). Прогоняем ещё раз
           — идемпотентно для нормального имени. */
        if (clean.length > 1) b = sanitize(b);
        if (!b) return '';
        var suffix = (ext == null || ext === '') ? '' : '.' + String(ext);
        b = truncBytes(b, Math.max(8, MAX_BYTES - utf8Len(suffix)));
        if (!b) return '';
        return b + suffix;
    }

    /**
     * Name a track from its tags.
     *   {artist, title} → "Artist - Title.ext"
     *   {title}         → "Title.ext"
     *   anything else   → '' (caller keeps the original name)
     * An artist with no title is deliberately NOT a name: ten tracks by one
     * artist would collapse onto one filename.
     */
    function fromTags(tags, originalName, opts) {
        tags = tags || {};
        opts = opts || {};
        var ext = opts.ext !== undefined ? opts.ext : split(originalName || '').ext;
        var title = String(tags.title == null ? '' : tags.title).trim();
        var artist = String(tags.artist == null ? '' : tags.artist).trim();
        if (!title) return '';
        var base = artist ? artist + ' - ' + title : title;
        return join(base, ext);
    }

    /**
     * Keep a name unique inside one container (a ZIP, one session's exports).
     * "x.mp3" → "x (2).mp3" → "x (3).mp3" — the shape Chrome/Android already
     * use, so it reads as a copy rather than as garbage.
     * `used` is a Set or a plain object used as a set; it is NOT mutated.
     *
     * ⚠️ Two holes closed 2026-09-26 (found while fixing /split-pdf, whose
     * archive lost every part but the last to one shared name):
     *   1. A name already at the byte cap came back UNCHANGED. The suffix was
     *      glued on first and join() then cut the name back to the cap — the
     *      suffix went with it, every candidate equalled the name, the loop ran
     *      out at 999 and returned the duplicate. ~90 Cyrillic letters is
     *      enough (a long track title, a bookmark). Now the base is cut to
     *      leave room, and the suffix is appended after.
     *   2. "Part.pdf" and "part.pdf" were distinct here and ONE file on
     *      Windows and macOS — unpacking or a folder write (FSA getFileHandle
     *      on NTFS) overwrites the first with the second. The comparison is
     *      case-insensitive now.
     */
    function distinct(name, used) {
        if (!used) return name;
        var lower = null;
        var has = function (n) {
            if (typeof used.has === 'function' ? used.has(n) : Object.prototype.hasOwnProperty.call(used, n)) return true;
            if (!lower) {
                lower = Object.create(null);
                var keys = [];
                // Set.forEach hands (value, value); a plain object has keys.
                if (typeof used.forEach === 'function') used.forEach(function (v, k) { keys.push(k); });
                else keys = Object.keys(used);
                for (var j = 0; j < keys.length; j++) lower[String(keys[j]).toLowerCase()] = 1;
            }
            return lower[String(n).toLowerCase()] === 1;
        };
        if (!has(name)) return name;
        var p = split(name), i = 2, cand;
        var base = sanitize(p.base);
        var extPart = p.ext ? '.' + p.ext : '';
        do {
            var suffix = ' (' + i + ')';
            var room = Math.max(1, MAX_BYTES - utf8Len(suffix + extPart));
            cand = (base ? truncBytes(base, room) + suffix : '(' + i + ')') + extPart;
            i++;
        } while (has(cand) && i < 100000);
        return cand;
    }

    /* ── parse(): the way back — a filename into tags ───────────────────
     * The tool has had «Разобрать имя файла» since the start, but it pattern-
     * matched the RAW name, and raw names are mostly not "Artist - Title".
     * Measured 2026-09-05 on 18 700 files exported from /audio-metadata (RU,
     * 30 d): 26 % use underscores for spaces, 6.7 % carry a site tag
     * («(mp3cut.net)», «(hitmos.fm)», «Zakachai1.ru - »), 6.5 % start with a
     * track number, 5.6 % end in the OS copy suffix «(1)», and the
     * «Artist_-_Title_81821385» shape (a download site's numeric id glued to
     * the title) is everywhere. The old parser handed all of that to the
     * title field verbatim.
     *
     * clean() strips what is provably not part of a track's identity; parse()
     * then splits on the separator patterns. Nothing here invents data: when
     * no separator is found, the cleaned name becomes the title only if the
     * cleaning actually changed something, a name with no letters in it is
     * never a title, and null means "leave the fields alone". Cases live in
     * tests/node/output-filename.test.mjs.
     */
    var SITE_TLD = '(?:net|com|ru|org|fm|io|me|info|biz|cc|to|su|ua|kz|by|uz|tv|ws|site|online|club|pro|app|xyz|top|zone|space|store|music|audio)';
    // "(mp3cut.net)", "[hitmos.fm]", "(mp3cut(1).net)", "site.ru - ", " - site.ru"
    var RE_SITE_GROUP = new RegExp('\\s*[\\(\\[][^()\\[\\]]*?[\\w-]+(?:\\(\\d+\\))?\\.' + SITE_TLD + '\\b[^()\\[\\]]*[\\)\\]]', 'gi');
    var RE_SITE_LEAD  = new RegExp('^\\s*(?:www\\.)?[\\w-]+\\.' + SITE_TLD + '\\s*[-–—_:]+\\s*', 'i');
    var RE_SITE_TAIL  = new RegExp('\\s*[-–—_]+\\s*(?:www\\.)?[\\w-]+\\.' + SITE_TLD + '\\s*$', 'i');
    var RE_SITE_BARE  = new RegExp('\\s+(?:www\\.)?[\\w-]+\\.' + SITE_TLD + '\\b', 'gi');
    var RE_LETTER;
    try { RE_LETTER = new RegExp('\\p{L}', 'u'); } catch (_) { RE_LETTER = /[A-Za-zА-Яа-яЁё]/; }
    function clean(base) {
        var s = String(base == null ? '' : base);
        s = s.replace(/\s*\(\d{1,2}\)\s*$/, '');                                   // OS copy suffix "(1)" — first, it hides the shape
        // underscores as spaces — the download-site convention ("Artist_-_Title");
        // a name with real spaces keeps its underscores unless they clearly are
        // the separator (an "_-_" run, or more of them than spaces)
        var us = (s.match(/_/g) || []).length, sp = (s.match(/\s/g) || []).length;
        if (us && (sp === 0 || /_-_/.test(s) || us > sp)) s = s.replace(/_+/g, ' ');
        s = s.replace(RE_SITE_GROUP, ' ').replace(RE_SITE_LEAD, '').replace(RE_SITE_TAIL, '').replace(RE_SITE_BARE, ' ');
        s = s.replace(/\s*[\(\[]\s*(?:\d{2,3}\s?kbps|\d{2,3}k)\s*[\)\]]/gi, ' ');   // "[320kbps]"
        s = s.replace(/[\s_-]+\d{2,3}\s?kbps\b/gi, ' ');                            // "_320kbps_"
        s = s.replace(/\b1[6-8]\d{11}\b/g, ' ');                                    // JS epoch (ms) stamped by an app
        s = s.replace(/\b20\d\d[-_.]\d\d[-_.]\d\d(?:[\sT_]\d\d[-_:.]\d\d(?:[-_:.]\d\d)?)?\b/g, ' '); // ISO-ish date/time stamps
        s = s.replace(/[\s_-]+\d{6,12}\s*$/, '');                                   // download-site numeric id at the end
        s = s.replace(/^\s*\d{2,4}\s*\(\d+\)\s*/, '');                              // "0808 (1)Little V." leading junk
        s = s.replace(/\s+/g, ' ').replace(/\s*[-–—]\s*$/, '').replace(/^\s*[-–—]\s*/, '').trim();
        return s;
    }
    function parse(name) {
        var base = split(name).base;
        var s = clean(base);
        if (!s || !RE_LETTER.test(s)) return null;
        var t = function (x) { return String(x || '').trim(); };
        var m;
        if ((m = s.match(/^(.+?)\s*[-–—]\s*(.+?)\s*[-–—]\s*(\d{1,3})\s*[-–—.]\s*(.+)$/)))      // Artist - Album - 01 - Title
            return { artist: t(m[1]), album: t(m[2]), track: t(m[3]), title: t(m[4]), cleaned: s };
        if ((m = s.match(/^(\d{1,3})\s*[.\-–—)]\s*(.+?)\s*[-–—]\s*(.+)$/)))                   // 01. Artist - Title
            return { track: t(m[1]), artist: t(m[2]), title: t(m[3]), cleaned: s };
        if ((m = s.match(/^(.+?)\s*[-–—]\s*(\d{1,3})\s*[.\-–—)]\s*(.+)$/)))                   // Artist - 01. Title
            return { artist: t(m[1]), track: t(m[2]), title: t(m[3]), cleaned: s };
        if ((m = s.match(/^(\d{1,3})\s*[.\-–—)]\s+(.+)$/)))                                    // 01 - Title
            return { track: t(m[1]), title: t(m[2]), cleaned: s };
        if ((m = s.match(/^(\d{1,2})\s+(\D.+?)\s+[-–—]\s+(.+)$/)))                             // 01 Artist - Title
            return { track: t(m[1]), artist: t(m[2]), title: t(m[3]), cleaned: s };
        if ((m = s.match(/^(.+?)\s+[-–—]\s+(.+)$/)) || (m = s.match(/^([^-–—]+?)\s*[–—]\s*(.+)$/))) // Artist - Title
            return { artist: t(m[1]), title: t(m[2]), cleaned: s };
        if ((m = s.match(/^(\d{1,2})\s+(\D.+)$/)))                                             // 01 Title
            return { track: t(m[1]), title: t(m[2]), cleaned: s };
        if (s !== base.trim()) return { title: s, cleaned: s };                              // cleaning alone was worth it
        return null;
    }

    return {
        split: split,
        sanitize: sanitize,
        join: join,
        fromTags: fromTags,
        distinct: distinct,
        clean: clean,
        parse: parse,
        utf8Len: utf8Len,
        // Exported because a caller composing several components has to cap
        // each one, and a CHARACTER cap is not a byte cap: 48 CJK characters
        // are 144 bytes, so two of them plus an extension blow the 255-byte
        // filename limit. Counting characters is the mistake this prevents.
        truncBytes: truncBytes,
        MAX_BYTES: MAX_BYTES
    };
});
